import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import SwyperLogo from '../components/SwypeRLogo';
import ImageUpload from '../components/ImageUpload';
import { Check, X, Loader2 } from 'lucide-react';
import { authAPI } from '../api/endpoints';
import { profileAPI } from '../api/endpoints';

const ProfileCompletion = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const usernameCheckTimeout = useRef(null);

  const [formData, setFormData] = useState({
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ').slice(1).join(' ') || '',
    username: user?.username || '',
    photos: user?.photos || []
  });

  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');

  const [usernameStatus, setUsernameStatus] = useState({
    checking: false,
    available: null,
    message: ''
  });

  const checkUsernameAvailability = async (username) => {
    if (!username || username.length === 0) {
      setUsernameStatus({ checking: false, available: null, message: '' });
      return;
    }

    // If username hasn't changed from current, mark as available
    if (username === user?.username) {
      setUsernameStatus({
        checking: false,
        available: true,
        message: 'Current username'
      });
      return;
    }

    try {
      setUsernameStatus({ checking: true, available: null, message: '' });
      const result = await authAPI.checkUsername(username);
      setUsernameStatus({
        checking: false,
        available: result.available,
        message: result.message
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to check username:', error);
      }
      setUsernameStatus({ checking: false, available: null, message: '' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Debounced username check
    if (name === 'username') {
      if (usernameCheckTimeout.current) {
        clearTimeout(usernameCheckTimeout.current);
      }

      if (value.length > 0) {
        usernameCheckTimeout.current = setTimeout(() => {
          checkUsernameAvailability(value);
        }, 500);
      } else {
        setUsernameStatus({ checking: false, available: null, message: '' });
      }
    }
  };

  const handlePhotosChange = (newPhotos) => {
    setFormData(prev => ({ ...prev, photos: newPhotos }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.username || !birthDay || !birthMonth || !birthYear) {
      toast({
        title: "Missing Information",
        description: "Please complete all required fields",
        variant: "destructive"
      });
      return;
    }

    if (usernameStatus.available === false) {
      toast({
        title: "Username Unavailable",
        description: usernameStatus.message || "This username is already taken",
        variant: "destructive"
      });
      return;
    }

    if (formData.photos.length === 0) {
      toast({
        title: "Photo Required",
        description: "Please upload a profile photo",
        variant: "destructive"
      });
      return;
    }

    // Calculate age from DOB
    const dateOfBirth = `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    if (age < 18) {
      toast({
        title: "Age Requirement",
        description: "You must be at least 18 years old to use Swyper",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const fullName = `${formData.firstName} ${formData.lastName}`;

      // Update profile
      const updatedUser = await profileAPI.updateProfile({
        name: fullName,
        username: formData.username,
        age: age,
        date_of_birth: dateOfBirth,
        photos: formData.photos
      });

      // Update local user state with the response
      setUser(updatedUser);

      toast({
        title: "Profile Complete!",
        description: "Welcome to Swyper!",
      });

      // Navigate to swipe page
      navigate('/swipe', { replace: true });

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Profile completion failed:', error);
      }
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to complete profile",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Generate day, month, year options
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 83 }, (_, i) => currentYear - 18 - i);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg pt-2 px-8 pb-8">
          <div className="flex flex-col items-center mb-4">
            <div className="mb-0 flex justify-center w-full">
              <SwyperLogo width={300} height={320} />
            </div>
            
            <div className="mb-2 mt-[-168px]">
              <p className="text-gray-600 font-bold text-xl mt-1">Complete Your Profile</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            {/* Username */}
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <Input
                  id="username"
                  name="username"
                  type="text"
                  placeholder="Choose a username"
                  maxLength={15}
                  value={formData.username}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  className={
                    usernameStatus.available === true
                      ? 'border-green-500 pr-10'
                      : usernameStatus.available === false
                      ? 'border-red-500 pr-10'
                      : ''
                  }
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {usernameStatus.checking && (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  )}
                  {!usernameStatus.checking && usernameStatus.available === true && (
                    <Check className="w-4 h-4 text-green-500" />
                  )}
                  {!usernameStatus.checking && usernameStatus.available === false && (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                </div>
              </div>
              {usernameStatus.message && (
                <p className={`text-xs ${usernameStatus.available ? 'text-green-600' : 'text-red-600'}`}>
                  {usernameStatus.message}
                </p>
              )}
            </div>

            {/* Date of Birth */}
            <div className="space-y-2">
              <Label>Date of Birth</Label>
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  disabled={isLoading}
                  required
                >
                  <option value="">Day</option>
                  {days.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>

                <select
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  disabled={isLoading}
                  required
                >
                  <option value="">Month</option>
                  {months.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>

                <select
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  disabled={isLoading}
                  required
                >
                  <option value="">Year</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Profile Photo */}
            <div className="space-y-2">
              <Label>Profile Photo</Label>
              <ImageUpload
                photos={formData.photos}
                onChange={handlePhotosChange}
                maxPhotos={1}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
              disabled={isLoading || usernameStatus.available === false}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Completing...
                </>
              ) : (
                'Complete Profile'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletion;
